import { ApiSet, BaseResponse, IndexApiSet, useDeleteApi, useShowApi, useIndexApi, usePostApi, usePutApi } from 'utils/network/api_hooks'
import { Form, useEffectSkipFirst } from 'utils/hooks'
import { CraifHttpClient } from 'utils/network/axios'
import { PagingResponse } from 'entities'
import { TrainingContent, TrainingContentForm, TrainingContentSearchForm } from 'entities/training_content'

type TrainingContentsResponse = PagingResponse & {
    results: TrainingContent[]
}

export function useFetchTrainingContentsApi(searchForm?: Form<TrainingContentSearchForm>): IndexApiSet<TrainingContentsResponse> & { execute: () => void } {
    const apiPath = 'training_contents/'
    const api = useIndexApi<TrainingContentsResponse>(new CraifHttpClient(), {
        initialResponse: { count: 0, results: [] },
        initialState: { page: searchForm?.object?.page || 1, perPage: searchForm?.object?.perPage || 50 },
    })

    const execute = (): void => {
        api.execute(apiPath, { params: searchForm?.object })
    }

    useEffectSkipFirst(() => {
        searchForm?.update((f) => {
            if (api.pageSet.page) f.page = api.pageSet.page
            if (api.pageSet.perPage) f.perPage = api.pageSet.perPage
        })
    }, [api.pageSet.page, api.pageSet.perPage])

    useEffectSkipFirst(() => {
        execute()
    }, [searchForm?.object])

    return { ...api, execute: execute }
}

export function useFetchTrainingContentApi(): ApiSet<BaseResponse> & { execute: (id: number) => void } {
    const apiSet = useShowApi<BaseResponse>(new CraifHttpClient(), {
        initialResponse: {},
    })

    const execute = (id: number) => {
        const apiPath = `training_contents/${id}/`
        apiSet.execute(apiPath)
    }

    return { ...apiSet, execute: execute }
}

export function usePostTrainingContentApi(): ApiSet<BaseResponse> & { execute: (form: Form<TrainingContentForm>) => void } {
    const apiSet = usePostApi<BaseResponse, TrainingContentForm>(
        new CraifHttpClient(),
        {
            initialResponse: {},
        },
        { formatJson: true }
    )

    const execute = (form: Form<TrainingContentForm>) => {
        const apiPath = `training_contents/`
        apiSet.execute(apiPath, form)
    }

    return { ...apiSet, execute: execute }
}

export function usePutTrainingContentApi(): ApiSet<BaseResponse> & { execute: (form: Form<TrainingContentForm>) => void } {
    const apiSet = usePutApi<BaseResponse, TrainingContentForm>(
        new CraifHttpClient(),
        {
            initialResponse: {},
        },
        { formatJson: true }
    )

    const execute = (form: Form<TrainingContentForm>) => {
        const apiPath = `training_contents/${form.object.id}/`
        apiSet.execute(apiPath, form.object)
    }

    return { ...apiSet, execute: execute }
}

export function useBulkPutTrainingContentsApi(): ApiSet<BaseResponse> & { execute: (form: Form<TrainingContentForm[]>) => void } {
    const apiSet = usePutApi<BaseResponse, TrainingContentForm[]>(
        new CraifHttpClient(),
        {
            initialResponse: {},
        },
        { formatJson: true }
    )

    const execute = (form: Form<TrainingContentForm[]>) => {
        const apiPath = `training_contents/bulk_update/`
        apiSet.execute(apiPath, form.object)
    }

    return { ...apiSet, execute: execute }
}

export function useDeleteTrainingContentApi(): ApiSet<BaseResponse> & {
    execute: (id: number) => void
} {
    const api = useDeleteApi<BaseResponse>(new CraifHttpClient(), {
        initialResponse: {},
    })

    const execute = (id: number): void => {
        const apiPath = `training_contents/${id}/`
        api.execute(apiPath)
    }

    return { ...api, execute: execute }
}
